'use client';

import { useTranslations } from 'next-intl';
import type { FindingEditRunCollection } from './findingEditForm.types';
import {
  Badge,
  Combobox,
  IconButton,
  Input,
  MetaDefault,
} from '@terra/ui-design-system';
import {
  ArrowUpDownIcon,
  BotIcon,
  Cancel01Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import { HStack, VStack } from '@terra/ui-styled-system';

type FindingEditRunComboboxProps = {
  collection: FindingEditRunCollection;
  selectedValue: string | null;
  isSearching: boolean;
  onValueChange: (traceThreadId: string | null) => void;
  onInputValueChange: (query: string) => void;
};

export function FindingEditRunCombobox({
  collection,
  selectedValue,
  isSearching,
  onValueChange,
  onInputValueChange,
}: FindingEditRunComboboxProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.assignment.fields.linkedRun',
  );

  return (
    <HStack gap={1} alignItems="center" w="full">
      <Combobox.Root
        collection={collection}
        value={selectedValue ? [selectedValue] : []}
        onValueChange={(details) => onValueChange(details.value[0] || null)}
        onInputValueChange={(details) => onInputValueChange(details.inputValue)}
        size="sm"
        flex={1}
        minW={0}
        w="full"
      >
        <Combobox.Control>
          <Combobox.Input placeholder={t('placeholder')} asChild>
            <Input
              size="sm"
              minW={0}
              pr="11"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            />
          </Combobox.Input>
          <Combobox.Trigger asChild>
            <IconButton variant="link" aria-label={t('label')} size="xs">
              <ArrowUpDownIcon size={14} />
            </IconButton>
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content maxH="300px" overflowY="auto">
            <Combobox.List>
              {isSearching ? (
                <MetaDefault p={2} textAlign="center" color="fg.muted">
                  {t('searching')}
                </MetaDefault>
              ) : collection.items.length > 0 ? (
                collection.items.map((item) => (
                  <Combobox.Item
                    key={item.value}
                    item={item}
                    height="auto"
                    py={2.5}
                  >
                    <HStack gap={2} alignItems="center" flex={1} minW={0}>
                      <BotIcon size={16} />
                      <VStack
                        gap={0.5}
                        alignItems="flex-start"
                        flex={1}
                        minW={0}
                      >
                        <HStack gap={1} w="full">
                          <Combobox.ItemText
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            flex={1}
                          >
                            {item.label}
                          </Combobox.ItemText>
                          <Badge size="sm" variant="fill" colorPalette="gray">
                            {t('traceLabel')}
                          </Badge>
                        </HStack>
                        {item.subtitle && (
                          <MetaDefault color="fg.muted">
                            {item.subtitle}
                          </MetaDefault>
                        )}
                      </VStack>
                      <Combobox.ItemIndicator>
                        <Tick01Icon size={14} />
                      </Combobox.ItemIndicator>
                    </HStack>
                  </Combobox.Item>
                ))
              ) : (
                <MetaDefault p={2} textAlign="center" color="fg.muted">
                  {t('typeToSearch')}
                </MetaDefault>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {selectedValue && (
        <IconButton
          variant="outline"
          size="xs"
          flexShrink={0}
          onClick={() => onValueChange(null)}
          aria-label={t('clearAriaLabel')}
        >
          <Cancel01Icon size={14} />
        </IconButton>
      )}
    </HStack>
  );
}
